import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Copy, CheckCircle2, Clock, Inbox } from "lucide-react";
import { toast } from "sonner";
import { money, logoUrl, api } from "@/lib/api";
import { PartnerGuides } from "@/components/PartnerGuides";

const STAGE = { received: ["Received", "bg-slate-100 text-slate-700", Inbox], in_progress: ["In progress", "bg-amber-100 text-amber-800", Clock], ready: ["Ready", "bg-emerald-100 text-emerald-800", CheckCircle2] };

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className="num text-2xl font-bold" data-testid={testId}>{value}</p></div>
);

const Row = ({ r }) => {
  const [label, cls, Icon] = STAGE[r.progress] || STAGE.received;
  return (
    <tr className="border-t border-slate-200" data-testid={`partner-dash-row-${r.id}`}>
      <td className="px-4 py-3"><p className="font-semibold">{r.name}</p>{r.company && <p className="text-xs text-slate-500">{r.company}</p>}</td>
      <td data-label="Study" className="px-4 py-3 text-sm">{r.tier_label}</td>
      <td data-label="Loan" className="px-4 py-3 text-sm">{r.loan_type_label || "—"}{r.loan_amount ? <span className="block text-xs text-slate-500">{money(r.loan_amount, r.currency)}</span> : null}</td>
      <td data-label="Referred" className="px-4 py-3 text-sm">{r.created_at.slice(0, 10)}</td>
      <td data-label="Needed by" className="px-4 py-3 text-sm">{r.deadline || "—"}</td>
      <td data-label="Status" className="px-4 py-3"><span data-testid={`partner-dash-status-${r.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}><Icon className="h-3.5 w-3.5" />{label}</span></td>
      <td data-label="Deposit" className="px-4 py-3 text-sm" data-testid={`partner-dash-deposit-${r.id}`}>{r.deposit_paid ? <span className="text-emerald-700 font-semibold">Paid</span> : <span className="text-slate-400">—</span>}</td>
    </tr>
  );
};

export default function PartnerDashboard() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get(`/public/partner-dashboard/${token}`).then((r) => setD(r.data)).catch(() => setError(true)); }, [token]);
  if (error) return <div className="min-h-screen grid place-items-center text-slate-400" data-testid="partner-dash-not-found">This dashboard link is not valid.</div>;
  if (!d) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  const refLink = `${window.location.origin}/refer/${d.partner.code}`;
  const count = (k) => d.referrals.filter((r) => r.progress === k).length;
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 py-4 sm:py-10 px-3 sm:px-4" data-testid="partner-dashboard">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-5 sm:p-8 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Partner dashboard</p>
            <h1 className="font-display text-2xl font-bold" data-testid="partner-dash-name">{d.partner.name}{d.partner.bank ? `, ${d.partner.bank}` : ""}</h1>
          </div>
          {d.has_logo ? <img src={logoUrl("partner")} alt={d.company_name} className="max-h-12 max-w-[180px] object-contain" /> : <span className="font-display text-xl font-bold">{d.company_name}</span>}
        </div>
        <div className="p-5 sm:p-8 space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Referred" value={d.referrals.length} testId="partner-dash-total" />
            <Stat label="Received" value={count("received")} testId="partner-dash-received" />
            <Stat label="In progress" value={count("in_progress")} testId="partner-dash-in-progress" />
            <Stat label="Ready" value={count("ready")} testId="partner-dash-ready" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm"><span className="font-semibold">Your referral link:</span> <span className="text-slate-600 break-all">{refLink}</span></p>
            <button type="button" data-testid="partner-dash-copy" onClick={() => navigator.clipboard.writeText(refLink).then(() => toast.success("Link copied")).catch(() => toast.error(refLink))}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-700"><Copy className="h-3.5 w-3.5" />Copy link</button>
          </div>
          {d.referrals.length === 0 ? <p className="text-center text-slate-500 py-10" data-testid="partner-dash-empty">No referrals yet. Share your link with borrowers who need a feasibility study.</p> : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[760px] m-cards" data-testid="partner-dash-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Borrower", "Study", "Loan", "Referred", "Needed by", "Status", "Deposit"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
                <tbody>{d.referrals.map((r) => <Row key={r.id} r={r} />)}</tbody>
              </table>
            </div>
          )}
          <PartnerGuides guides={d.guides} code={d.partner.code} partnerName={d.partner.name} />
          <p className="text-xs text-slate-400">This page is private to you. Study contents are shared only with the borrower.</p>
        </div>
      </div>
    </div>
  );
}
